import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const card = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

// B&W line illustrations, index-aligned with the sector stories (0..2).
const SECTOR_ART = [
  "https://static.prod-images.emergentagent.com/jobs/10ac93bc-74b2-419b-82fd-5c7d03bf86f2/images/fee0b2346918431cdeaf91c57fe0abd78bfba59d671c39c59f5c6efc1f76dcbd.png",
  "https://static.prod-images.emergentagent.com/jobs/10ac93bc-74b2-419b-82fd-5c7d03bf86f2/images/8f000eb137a36b3b9c2941909e8906e831598f8bd4fe0ced2964dce11d34d27c.png",
  "https://static.prod-images.emergentagent.com/jobs/10ac93bc-74b2-419b-82fd-5c7d03bf86f2/images/5979e180df3b318293429e0edc93e25860312c15608110d3c1bbae0c44193178.png",
];

export const SectorOpportunities = () => {
  const { t } = useLang();
  const S = t.sectors;
  return (
    <section id="sectors" className="relative py-24 sm:py-32 bg-[var(--page)] text-[var(--ink)]" data-testid="sectors-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[var(--ink)]" />
            <span className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[var(--muted-ink)] font-semibold">{S.overline}</span>
          </div>
          <h2 className="font-display font-semibold tracking-tight leading-[1.03] text-3xl sm:text-4xl lg:text-5xl">{S.title}</h2>
          <p className="mt-6 text-base sm:text-lg text-[var(--muted-ink)] leading-relaxed">{S.sub}</p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-px bg-[var(--hairline)] border border-[var(--hairline)]">
          {S.stories.map((s, i) => (
            <motion.figure
              key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} variants={card}
              data-testid={`success-story-${i}`}
              className="bg-[var(--page)] p-8 sm:p-10 flex flex-col"
            >
              <div className="mb-6 flex items-center justify-center bg-[var(--ink)]/[0.03] rounded-[3px] border border-[var(--hairline)] aspect-[5/3] overflow-hidden">
                <img
                  src={SECTOR_ART[i % SECTOR_ART.length]}
                  alt={`${s.sector} — line illustration`}
                  data-testid={`sector-art-${i}`}
                  loading="lazy"
                  className="h-full w-full object-contain p-4 mix-blend-multiply"
                />
              </div>
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[var(--muted-ink)]">{s.sector}</span>
              <Quote className="h-7 w-7 mt-6 text-[var(--ink)]/25" strokeWidth={1.5} />
              <blockquote className="mt-4 font-display text-lg sm:text-xl leading-snug flex-1">"{s.quote}"</blockquote>
              <figcaption className="mt-6 pt-5 border-t border-[var(--hairline)]">
                <div className="font-semibold">{s.name}</div>
                <div className="mt-1 text-sm text-[var(--muted-ink)]">{s.detail}</div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
        <p className="mt-6 text-xs text-[var(--muted-ink)] italic">{S.note}</p>
      </div>
    </section>
  );
};
